import { apiClient } from '@/shared/api';

import type {
  BindPublicationOverlayCatalogRequest,
  CatalogPreviewResult,
  CreatePublicationCatalogRequest,
  CreatePublicationCatalogRuleRequest,
  EffectivePublicationProfile,
  PublicationCatalog,
  PublicationCatalogChannelBinding,
  PublicationCatalogRule,
  PublicationCatalogType,
  PublicationProfileOverride,
  PublicationProfileOverrideRequest,
  PublicationRun,
  PublicationRunDetail,
  PublicationRunsPage,
  PublicationSalesChannel,
  SetPublicationPrimaryCatalogRequest,
  UpdatePublicationCatalogRequest,
  UpdatePublicationCatalogRuleRequest,
} from '../model/publicationTypes';

const BASE = '/api/v1/catalog/publication';

// ── Catalogs ─────────────────────────────────────────────────────────────────

export const listPublicationCatalogs = async (): Promise<PublicationCatalog[]> =>
  (await apiClient.get<PublicationCatalog[]>(`${BASE}/catalogs`)).data;

export const getPublicationCatalog = async (catalogId: string): Promise<PublicationCatalog> =>
  (await apiClient.get<PublicationCatalog>(`${BASE}/catalogs/${catalogId}`)).data;

export const createPublicationCatalog = async (
  body: CreatePublicationCatalogRequest,
): Promise<PublicationCatalog> =>
  (await apiClient.post<PublicationCatalog>(`${BASE}/catalogs`, body)).data;

/** `code`/`lifecycleKind`/`catalogTypeCode`/`validFrom`/`validTo`/`status` are immutable here. */
export const updatePublicationCatalog = async (
  catalogId: string,
  body: UpdatePublicationCatalogRequest,
): Promise<PublicationCatalog> =>
  (await apiClient.put<PublicationCatalog>(`${BASE}/catalogs/${catalogId}`, body)).data;

export const activatePublicationCatalog = async (catalogId: string): Promise<PublicationCatalog> =>
  (await apiClient.post<PublicationCatalog>(`${BASE}/catalogs/${catalogId}/activate`)).data;

/** Permanent catalogs only — temporary catalogs are rejected with `400`. */
export const pausePublicationCatalog = async (catalogId: string): Promise<PublicationCatalog> =>
  (await apiClient.post<PublicationCatalog>(`${BASE}/catalogs/${catalogId}/pause`)).data;

export const resumePublicationCatalog = async (catalogId: string): Promise<PublicationCatalog> =>
  (await apiClient.post<PublicationCatalog>(`${BASE}/catalogs/${catalogId}/resume`)).data;

/** Terminal state — there is no delete endpoint for a publication catalog, only archive. */
export const archivePublicationCatalog = async (catalogId: string): Promise<PublicationCatalog> =>
  (await apiClient.post<PublicationCatalog>(`${BASE}/catalogs/${catalogId}/archive`)).data;

/** Synchronous full recomposition + republish; creates a new run with `trigger: MANUAL`. */
export const republishPublicationCatalog = async (catalogId: string): Promise<PublicationRun> =>
  (await apiClient.post<PublicationRun>(`${BASE}/catalogs/${catalogId}/republish`)).data;

/** Dry-run membership preview — no side effects, not server-paginated (full result in one call). */
export const previewPublicationCatalog = async (catalogId: string): Promise<CatalogPreviewResult> =>
  (await apiClient.get<CatalogPreviewResult>(`${BASE}/catalogs/${catalogId}/preview`)).data;

export const listPublicationRuns = async (
  catalogId: string,
  params: { page?: number; size?: number } = {},
): Promise<PublicationRunsPage> =>
  (
    await apiClient.get<PublicationRunsPage>(`${BASE}/catalogs/${catalogId}/publication-runs`, {
      params,
    })
  ).data;

export const getPublicationRunDetail = async (
  catalogId: string,
  runId: string,
): Promise<PublicationRunDetail> =>
  (
    await apiClient.get<PublicationRunDetail>(
      `${BASE}/catalogs/${catalogId}/publication-runs/${runId}`,
    )
  ).data;

// ── Rules ────────────────────────────────────────────────────────────────────

export const listPublicationCatalogRules = async (
  catalogId: string,
): Promise<PublicationCatalogRule[]> =>
  (await apiClient.get<PublicationCatalogRule[]>(`${BASE}/catalogs/${catalogId}/rules`)).data;

export const createPublicationCatalogRule = async (
  catalogId: string,
  body: CreatePublicationCatalogRuleRequest,
): Promise<PublicationCatalogRule> =>
  (
    await apiClient.post<PublicationCatalogRule>(`${BASE}/catalogs/${catalogId}/rules`, body)
  ).data;

/** Rules are replaced wholesale — the body fully replaces the current rule. */
export const updatePublicationCatalogRule = async (
  catalogId: string,
  ruleId: string,
  body: UpdatePublicationCatalogRuleRequest,
): Promise<PublicationCatalogRule> =>
  (
    await apiClient.put<PublicationCatalogRule>(
      `${BASE}/catalogs/${catalogId}/rules/${ruleId}`,
      body,
    )
  ).data;

export const deletePublicationCatalogRule = async (
  catalogId: string,
  ruleId: string,
): Promise<void> => {
  await apiClient.delete(`${BASE}/catalogs/${catalogId}/rules/${ruleId}`);
};

// ── Sales channels + catalog bindings ─────────────────────────────────────────

export const listPublicationSalesChannels = async (): Promise<PublicationSalesChannel[]> =>
  (await apiClient.get<PublicationSalesChannel[]>(`${BASE}/sales-channels`)).data;

export const getPublicationSalesChannel = async (
  channelId: string,
): Promise<PublicationSalesChannel> =>
  (await apiClient.get<PublicationSalesChannel>(`${BASE}/sales-channels/${channelId}`)).data;

export const listPublicationCatalogChannelBindings = async (
  channelId: string,
): Promise<PublicationCatalogChannelBinding[]> =>
  (
    await apiClient.get<PublicationCatalogChannelBinding[]>(
      `${BASE}/sales-channels/${channelId}/catalog-bindings`,
    )
  ).data;

/**
 * No update/delete endpoint exists for a sales channel — see `PublicationSalesChannel`'s doc
 * comment in `model/publicationTypes.ts`.
 */

/** Atomically replaces any existing PRIMARY binding of this channel in one call. */
export const setPublicationPrimaryCatalog = async (
  channelId: string,
  body: SetPublicationPrimaryCatalogRequest,
): Promise<PublicationCatalogChannelBinding> =>
  (
    await apiClient.put<PublicationCatalogChannelBinding>(
      `${BASE}/sales-channels/${channelId}/primary-catalog`,
      body,
    )
  ).data;

/** Re-binding an already-bound overlay updates its `priority` in place rather than duplicating. */
export const bindPublicationOverlayCatalog = async (
  channelId: string,
  catalogId: string,
  body: BindPublicationOverlayCatalogRequest,
): Promise<PublicationCatalogChannelBinding> =>
  (
    await apiClient.post<PublicationCatalogChannelBinding>(
      `${BASE}/sales-channels/${channelId}/overlay-catalogs/${catalogId}`,
      body,
    )
  ).data;

/** Idempotent — unbinding an already-unbound overlay also succeeds. */
export const unbindPublicationOverlayCatalog = async (
  channelId: string,
  catalogId: string,
): Promise<void> => {
  await apiClient.delete(`${BASE}/sales-channels/${channelId}/overlay-catalogs/${catalogId}`);
};

// ── Catalog types (platform registry, read-only) ─────────────────────────────

/** Active catalog types only — open read, not tenant-scoped. */
export const listPublicationCatalogTypes = async (): Promise<PublicationCatalogType[]> =>
  (await apiClient.get<PublicationCatalogType[]>(`${BASE}/catalog-types`)).data;

export const getEffectivePublicationProfile = async (
  code: string,
): Promise<EffectivePublicationProfile> =>
  (
    await apiClient.get<EffectivePublicationProfile>(
      `${BASE}/catalog-types/${encodeURIComponent(code)}/publication-profile`,
    )
  ).data;

export const putPublicationProfileOverride = async (
  code: string,
  body: PublicationProfileOverrideRequest,
): Promise<PublicationProfileOverride> =>
  (
    await apiClient.put<PublicationProfileOverride>(
      `${BASE}/catalog-types/${encodeURIComponent(code)}/publication-profile/override`,
      body,
    )
  ).data;

/** Removes the tenant's override (revert to the platform default) — idempotent. */
export const deletePublicationProfileOverride = async (code: string): Promise<void> => {
  await apiClient.delete(
    `${BASE}/catalog-types/${encodeURIComponent(code)}/publication-profile/override`,
  );
};
